import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ImageIcon,
  Target,
} from 'lucide-react';
import { auth } from '@/lib/firebase';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';

const DEFAULT_FROM = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split('T')[0];
})();
const DEFAULT_TO = new Date().toISOString().split('T')[0];

interface CampaignMetrics {
  id: string;
  name: string;
  status: string;
  objective: string;
  spend: number;
  impressions: number;
  clicks: number;
  ctr: number;
  cpm: number;
  reach: number;
  leads: number;
  cpl: number;
}

interface CampaignAttributionRow {
  campaign_id: string;
  campaign_name: string;
  spend: number;
  stages: {
    lead: number;
    scheduled: number;
    showed: number;
    qualified: number;
    proposal: number;
    closed_won: number;
  };
  revenue: number;
  roas: number;
  cost_per_close: number;
}

interface AdMetrics extends CampaignMetrics {
  creative_id?: string;
  creative_name?: string;
  creative_thumbnail_url?: string;
  creative_image_url?: string;
  creative_title?: string;
  creative_body?: string;
}

interface AccountSummary {
  spend: number;
  impressions: number;
  clicks: number;
  ctr: number;
  cpm: number;
  reach: number;
  leads: number;
  cpl: number;
  booked_calls: number;
  cost_per_booked_call: number;
  show_rate: number;
  showed_count: number;
  no_show_count: number;
  total_past_scheduled: number;
  date_from: string;
  date_to: string;
}

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

const fmt = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });
const fmtCurrency = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function CampaignAttribution({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }) {
  const [rows, setRows] = useState<CampaignAttributionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAttribution = async () => {
      setLoading(true);
      setError(null);
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Not authenticated');
        const token = await user.getIdToken();
        const headers = { 'Authorization': `Bearer ${token}` };
        const params = `?date_from=${dateFrom}&date_to=${dateTo}`;
        const res = await fetch(`${API_BASE_URL}/api/analytics/campaign-attribution${params}`, { headers });
        const data = await res.json();
        if (!cancelled) setRows(data.campaigns || []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to fetch attribution');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAttribution();
    return () => { cancelled = true; };
  }, [dateFrom, dateTo]);

  const STAGES = ['lead', 'scheduled', 'showed', 'qualified', 'proposal', 'closed_won'] as const;
  const STAGE_LABELS: Record<string, string> = {
    lead: 'Lead',
    scheduled: 'Sched.',
    showed: 'Showed',
    qualified: 'Qual.',
    proposal: 'Proposal',
    closed_won: 'Closed',
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Target className="h-4 w-4" />
          Campaign Attribution
        </CardTitle>
        <p className="text-xs text-muted-foreground">Which campaigns produce revenue, not just leads.</p>
      </CardHeader>
      <CardContent>
        {error && (
          <p className="text-sm text-yellow-600 mb-3">{error}</p>
        )}
        {loading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10" />)}
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No attributed prospects in this period. Leads need a <code>meta_campaign_id</code> to appear here.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground text-xs">
                  <th className="text-left py-2 pr-4 font-medium">Campaign</th>
                  <th className="text-right py-2 px-2 font-medium">Spend</th>
                  {STAGES.map(s => (
                    <th key={s} className="text-right py-2 px-2 font-medium">{STAGE_LABELS[s]}</th>
                  ))}
                  <th className="text-right py-2 px-2 font-medium">Revenue</th>
                  <th className="text-right py-2 pl-2 font-medium">ROAS</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.campaign_id} className={`border-b last:border-0 hover:bg-muted/30 ${idx % 2 === 1 ? 'bg-muted/10' : ''}`}>
                    <td className="py-2 pr-4 max-w-[200px]">
                      <div className="truncate font-medium">{row.campaign_name}</div>
                      {row.cost_per_close > 0 && (
                        <div className="text-xs text-muted-foreground">
                          {fmtCurrency(row.cost_per_close)} / close
                        </div>
                      )}
                    </td>
                    <td className="text-right py-2 px-2 tabular-nums">
                      {row.spend > 0 ? fmtCurrency(row.spend) : <span className="text-muted-foreground">—</span>}
                    </td>
                    {STAGES.map(s => (
                      <td key={s} className="text-right py-2 px-2 tabular-nums">
                        {row.stages[s] > 0
                          ? row.stages[s]
                          : <span className="text-muted-foreground">—</span>
                        }
                      </td>
                    ))}
                    <td className="text-right py-2 px-2 tabular-nums">
                      {row.revenue > 0
                        ? <span className="font-semibold text-green-600">{fmtCurrency(row.revenue)}</span>
                        : <span className="text-muted-foreground">—</span>
                      }
                    </td>
                    <td className="text-right py-2 pl-2 tabular-nums">
                      {row.roas > 0
                        ? <span className={row.roas >= 2 ? 'text-green-600 font-semibold' : 'text-yellow-600'}>
                            {row.roas.toFixed(1)}x
                          </span>
                        : <span className="text-muted-foreground text-xs">N/A</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function CampaignsView() {
  const [searchParams] = useSearchParams();
  const dateFrom  = searchParams.get('from')      ?? DEFAULT_FROM;
  const dateTo    = searchParams.get('to')         ?? DEFAULT_TO;
  const highlight = searchParams.get('highlight')  ?? null;

  useEffect(() => {
    if (!highlight) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(`section-${highlight}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
      setTimeout(() => el.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2'), 3000);
    }, 500);
    return () => clearTimeout(timer);
  }, [highlight]);

  const [summary, setSummary] = useState<AccountSummary | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignMetrics[]>([]);
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);
  const [campaignAds, setCampaignAds] = useState<Record<string, AdMetrics[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeader();
      const params = `?date_from=${dateFrom}&date_to=${dateTo}`;

      const [summaryRes, campaignsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/analytics/summary${params}`, { headers }),
        fetch(`${API_BASE_URL}/api/analytics/campaigns${params}`, { headers }),
      ]);

      const summaryData = await summaryRes.json();
      const campaignsData = await campaignsRes.json();

      if (summaryData.summary) setSummary(summaryData.summary);
      if (summaryData.error) setError(summaryData.error);
      setCampaigns(campaignsData.campaigns || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [dateFrom, dateTo]);

  const fetchAds = async (campaignId: string) => {
    if (campaignAds[campaignId]) return;
    try {
      const headers = await getAuthHeader();
      const params = `?date_from=${dateFrom}&date_to=${dateTo}`;
      const res = await fetch(`${API_BASE_URL}/api/analytics/campaigns/${campaignId}${params}`, { headers });
      const data = await res.json();
      setCampaignAds(prev => ({ ...prev, [campaignId]: data.ads || [] }));
    } catch (e) {
      console.error('Failed to fetch ads:', e);
    }
  };

  const toggleCampaign = (id: string) => {
    if (expandedCampaign === id) {
      setExpandedCampaign(null);
    } else {
      setExpandedCampaign(id);
      fetchAds(id);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <Card className="border-yellow-500/50 bg-yellow-500/10">
          <CardContent className="py-3 text-sm text-yellow-600">{error}</CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Total Spend</p>
              <p className="text-3xl font-bold leading-tight">{fmtCurrency(summary.spend)}</p>
            </CardContent>
          </Card>
          <Card id="section-cpl">
            <CardContent className="p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Total Leads</p>
              <p className="text-3xl font-bold leading-tight">{summary.leads}</p>
              <p className="text-xs text-muted-foreground mt-1">{fmtCurrency(summary.cpl)} per lead</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Booked Calls</p>
              <p className="text-3xl font-bold leading-tight">{summary.booked_calls}</p>
              <p className="text-xs text-muted-foreground mt-1">{fmtCurrency(summary.cost_per_booked_call)} per booking</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Impressions</p>
              <p className="text-3xl font-bold leading-tight">{fmt(summary.impressions)}</p>
              <p className="text-xs text-muted-foreground mt-1">{fmt(summary.reach)} reach</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Show Rate</p>
              <p className="text-3xl font-bold leading-tight">
                {summary.total_past_scheduled > 0 ? `${summary.show_rate}%` : '—'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {summary.total_past_scheduled > 0
                  ? `${summary.showed_count} showed / ${summary.total_past_scheduled} scheduled`
                  : 'No past meetings'}
              </p>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Campaign Attribution */}
      <CampaignAttribution dateFrom={dateFrom} dateTo={dateTo} />

      {/* Campaign List */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Campaigns</h2>
        {loading ? (
          [...Array(2)].map((_, i) => <Skeleton key={i} className="h-20" />)
        ) : campaigns.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No campaign data available. Ensure META_AD_ACCOUNT_ID is configured and the token has ads_read permission.
            </CardContent>
          </Card>
        ) : (
          campaigns.map(campaign => {
            const isZeroActivity = campaign.spend === 0 && campaign.leads === 0 && campaign.impressions === 0;
            return (
            <Card key={campaign.id} className={`overflow-hidden ${isZeroActivity ? 'opacity-60' : ''}`}>
              <button
                className="w-full text-left"
                onClick={() => toggleCampaign(campaign.id)}
              >
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {expandedCampaign === campaign.id ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                      <div>
                        <CardTitle className="text-base">{campaign.name}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant={campaign.status === 'ACTIVE' ? 'default' : 'secondary'} className="text-xs">
                            {campaign.status}
                          </Badge>
                          {isZeroActivity && (
                            <Badge variant="outline" className="text-xs text-muted-foreground">No data in period</Badge>
                          )}
                          <span className="text-xs text-muted-foreground">{campaign.objective}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-right">
                        <div className="font-semibold">{fmtCurrency(campaign.spend)}</div>
                        <div className="text-xs text-muted-foreground">spend</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{campaign.leads}</div>
                        <div className="text-xs text-muted-foreground">leads</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{fmtCurrency(campaign.cpl)}</div>
                        <div className="text-xs text-muted-foreground">CPL</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{fmt(campaign.impressions)}</div>
                        <div className="text-xs text-muted-foreground">impressions</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{campaign.ctr.toFixed(2)}%</div>
                        <div className="text-xs text-muted-foreground">CTR</div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </button>

              {/* Expanded: Per-ad breakdown */}
              {expandedCampaign === campaign.id && (
                <CardContent className="border-t bg-muted/30">
                  <div className="text-sm font-medium mb-3 pt-2">Ads</div>
                  {!campaignAds[campaign.id] ? (
                    <Skeleton className="h-16" />
                  ) : campaignAds[campaign.id].length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">No ads found</p>
                  ) : (
                    <div className="space-y-2">
                      {campaignAds[campaign.id].map(ad => (
                        <div key={ad.id} className="flex items-center gap-4 p-3 rounded-lg bg-background border">
                          {ad.creative_thumbnail_url ? (
                            <img
                              src={ad.creative_thumbnail_url}
                              alt={ad.creative_name || 'Ad creative'}
                              className="w-16 h-16 rounded object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-16 h-16 rounded bg-muted flex items-center justify-center flex-shrink-0">
                              <ImageIcon className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{ad.name}</div>
                            {ad.creative_title && (
                              <div className="text-xs text-muted-foreground truncate">{ad.creative_title}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm flex-shrink-0">
                            <div className="text-right">
                              <div className="font-medium">{fmtCurrency(ad.spend)}</div>
                              <div className="text-xs text-muted-foreground">spend</div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium">{ad.leads}</div>
                              <div className="text-xs text-muted-foreground">leads</div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium">{fmtCurrency(ad.cpl)}</div>
                              <div className="text-xs text-muted-foreground">CPL</div>
                            </div>
                            <div className="text-right">
                              <div className="font-medium">{ad.ctr.toFixed(2)}%</div>
                              <div className="text-xs text-muted-foreground">CTR</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );})
        )}
      </div>
    </div>
  );
}
